/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useContext } from 'react';
import { Axis, BlueprintInstanceType, Components, type CornerSize, COUNTERTOP_NONE_KEY, COUNTERTOP_TEXTURE_DIMENSION, Dimension, IslandType, Vertex3 } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core';
import { NON_APPLIANCE_CATEGORY_KEYS } from '../../context-menu/helpers/is-swappable-appliance';
import { RenderBreakpointsCountertop } from './render-breakpoints-countertop';
import { RenderCountertopCutoutDetailBlocks } from './render-countertop-cutout-detail-blocks';
import { renderTracking } from '@r-services/render-tracking';
import { showCountertopDil } from '../show/show-countertop.dil';
import { RenderCountertopTopModelLabels } from './render-countertop-top-model-labels';
import { ShowCountertopPanelItemGroups } from './render-countertop-panel-item-groups';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { usePanel } from '@r-hooks';

import { AddIslandActions, IslandActions } from '../../components/action-menu';
import { convertPolyPoints2Vectors, convertRect2Vectors } from '@r-core/helpers';
import { DevShowIntersectionAreas, DimensionLine } from '@r-components';
import { Layer, ShowDimensions } from '@r-core/types';

import '../../app/styles/blueprint-component.scss';
import { RenderCountertopMask } from './render-countertop-mask';

// "No Countertop" renders a placeholder slab using the gray clean-edge texture with a red
// tint so it clearly reads as a non-RTA countertop. The material key stays 'none', so it is
// excluded from pricing and never adds a clean-edge line item.
export const NON_COUNTERTOP_PLACEHOLDER_TEXTURE = 'CleanEdge_Slate_Tile.jpg';
export const NON_COUNTERTOP_TINT_COLOR = '#ff0000';
export const NON_COUNTERTOP_TINT_OPACITY = 0.15;

export type RenderCountertopProps = {
	islandId: string;
	offsetX?: number;
	offsetY?: number;
	/** Blueprint joint-countertop sheet only: neither top in a joint group shows its own depth (side-panel) dimension. */
	hideDepthDimension?: boolean;
	/** Blueprint joint-countertop sheet only: the row owns the length story (#551) -- one total, split at each seam -- so no member draws its own back-edge length. */
	hideLengthDimension?: boolean;
};

export const RenderCountertop = (props: RenderCountertopProps): React.ReactElement => {
	const {
		controller: { options, kitchen, navigation }
	} = useContext(AppContext);

	const { islandId, offsetX = 0, offsetY = 0, hideDepthDimension = false, hideLengthDimension = false } = props;
	const island = kitchen.design.islands.findById(islandId);
	const { frontPanelId } = island.panels;
	const [{ panel: frontPanel }, panelMeta] = usePanel({ panelId: frontPanelId, isCountertop: true });
	const devInsightsLayer = showCountertopDil({ island, options });
	const hasBreakPoints = panelMeta.breakpoints.length > 0;

	// (breakpoint segment dimensions are computed locally within the panel outside dimensions block)

	// surround texture
	const materialAsset = panelMeta.countertop.material;
	const materialScale = {
		height: materialAsset.scale.height * COUNTERTOP_TEXTURE_DIMENSION,
		width: materialAsset.scale.width * COUNTERTOP_TEXTURE_DIMENSION
	};
	const isNonCountertop = materialAsset.key === COUNTERTOP_NONE_KEY;
	const effectiveTexture = isNonCountertop ? NON_COUNTERTOP_PLACEHOLDER_TEXTURE : materialAsset.texture;

	const blueprintToApplianceOpacity = (100 - options.applianceOpacity) / 100;

	// Scoped per island so two countertops rendered side by side (the joint sheet) don't collide on id.
	const countertopTextureId = `counter-texture-${islandId}`;

	const underlyingPanels = island.panels.children.map((panel) => {
		const underlyingPanel = {
			panel,
			connections: {
				isTarget: (island.connections.children.find((connection) => {
					return !connection.isSource && connection.sourcePanelId === panel.id;
				}) ?? null) !== null,
				isSource: (island.connections.children.find((connection) => {
					return connection.isSource && connection.sourcePanelId === panel.id;
				}) ?? null) !== null
			},
			vectors: convertRect2Vectors({
				layer: Layer.Panel,
				center: { x: 0, y: 0 },
				width: panel.size.width,
				height: panel.thickness,
				angle: 0 // panel.getOriginAngle()
			})
		};
		// console.log('underlyingPanel', panel.label, panel.center, underlyingPanel.vectors);

		return underlyingPanel;
	});

	// countertop
	const countertop = island.getCountertop();
	const renderDetails = countertop.renderingDetails;
	const countertopVectors = convertPolyPoints2Vectors(renderDetails);
	const translate = {
		x: offsetX,
		y: offsetY
	};

	let showTexture = Boolean(effectiveTexture);
	let includeLabelBox = true;
	let useDimensionBox = true;
	const { showCountertops, showCorners, showPanelOutsideDimensions, showApplianceDimensions } = options;
	let { showAppliances, showCutouts } = options;

	// eslint-disable-next-line default-case
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
			showTexture = false;
			includeLabelBox = false;
			showAppliances = false;
			useDimensionBox = false;
			break;
		case DisplayMode.SkuOverhead:
			showAppliances = false;
			showCutouts = false;
			break;
	}

	// The cutout mask element only renders below when showCutouts is true — referencing a missing
	// mask id hides the whole masked element in SVG, so an unmasked (solid, no cutout holes) fill
	// is required here instead of a dangling mask reference.
	const countertopMaskUrl = showCutouts ? `url(#countertop-mask-${islandId})` : undefined;

	const horizontalIntersects = frontPanel.getHorizontalIntersectionAreas({ inside: !showAppliances, includeDuplicates: false, includeEnd2End: false, minHeight: 0.5, isCountertop: true })
		.map((area) => ({
			...area,
			shouldHide: false,
			dimension: kitchen.design.dimensions.find(area.intersectionReferenceIds, area.axis, true) as Dimension
		}));
	const verticalIntersects = frontPanel.getVerticalIntersectionAreas({ inside: !showAppliances, includeDuplicates: false, includeEnd2End: false, minWidth: 0.5, isCountertop: true })
		.map((area) => ({
			...area,
			shouldHide: false,
			dimension: kitchen.design.dimensions.find(area.intersectionReferenceIds, area.axis, true) as Dimension
		}))

	horizontalIntersects.forEach((current, currentIndex) => {
		if (!current.intersectionReferenceIds || current.intersectionReferenceIds.length < 2) {
			return;
		}

		const [currentFirstId, currentSecondId] = current.intersectionReferenceIds;

		// Find a smaller dimension sharing current's first ID (ensures we only hide the spanning/largest dimension)
		let firstMatch = null;
		for (let i = 0; i < horizontalIntersects.length; i++) {
			if (i === currentIndex) continue;

			const other = horizontalIntersects[i];
			if (!other.shouldHide &&
            other.intersectionReferenceIds &&
            other.intersectionReferenceIds.length >= 2 &&
            other.intersectionReferenceIds[0] === currentFirstId &&
            other.size.width < current.size.width) {
				firstMatch = other;
				break;
			}
		}

		// If we found a first match, create new intersection [matchedSecondId, currentSecondId] and check if it exists
		if (firstMatch) {
			const [_matchedFirstId, matchedSecondId] = firstMatch.intersectionReferenceIds;
			const newIntersectionToFind = [matchedSecondId, currentSecondId];

			const newIntersectionExists = horizontalIntersects.some((other) => {
				return other !== current &&
                other.intersectionReferenceIds &&
                other.intersectionReferenceIds.length >= 2 &&
                other.size.width < current.size.width &&
                ((other.intersectionReferenceIds[0] === newIntersectionToFind[0] && other.intersectionReferenceIds[1] === newIntersectionToFind[1]) ||
                 (other.intersectionReferenceIds[0] === newIntersectionToFind[1] && other.intersectionReferenceIds[1] === newIntersectionToFind[0]));
			});

			current.shouldHide = newIntersectionExists;
		}
	});

	verticalIntersects.forEach((current, currentIndex) => {
		if (!current.intersectionReferenceIds || current.intersectionReferenceIds.length < 2) {
			return;
		}

		const [currentFirstId, currentSecondId] = current.intersectionReferenceIds;

		// Find a smaller dimension sharing current's first ID (ensures we only hide the spanning/largest dimension)
		let firstMatch = null;
		for (let i = 0; i < verticalIntersects.length; i++) {
			if (i === currentIndex) continue;

			const other = verticalIntersects[i];
			if (!other.shouldHide &&
            other.intersectionReferenceIds &&
            other.intersectionReferenceIds.length >= 2 &&
            other.intersectionReferenceIds[0] === currentFirstId &&
            other.size.height < current.size.height) {
				firstMatch = other;
				break;
			}
		}

		// If we found a first match, create new intersection [matchedSecondId, currentSecondId] and check if it exists
		if (firstMatch) {
			const [_matchedFirstId, matchedSecondId] = firstMatch.intersectionReferenceIds;
			const newIntersectionToFind = [matchedSecondId, currentSecondId];

			const newIntersectionExists = verticalIntersects.some((other) => {
				return other !== current &&
                other.intersectionReferenceIds &&
                other.intersectionReferenceIds.length >= 2 &&
                other.size.height < current.size.height &&
                ((other.intersectionReferenceIds[0] === newIntersectionToFind[0] && other.intersectionReferenceIds[1] === newIntersectionToFind[1]) ||
                 (other.intersectionReferenceIds[0] === newIntersectionToFind[1] && other.intersectionReferenceIds[1] === newIntersectionToFind[0]));
			});

			current.shouldHide = newIntersectionExists;
		}
	});

	let intersectionAreas = horizontalIntersects.concat(verticalIntersects);

	// Short-circuit intersection dimensions referencing top-only appliances
	// (pizza ovens, asado smokers, etc.) in any MFG view — those appliances
	// are filtered from the panel-item-groups render, so their edge-to-cutout
	// dimensions shouldn't show either.
	const isMfgView = navigation.displayMode === DisplayMode.Manufacturing
		|| navigation.displayMode === DisplayMode.InlineManufacturing
		|| navigation.displayMode === DisplayMode.OverheadManufacturing
		|| navigation.displayMode === DisplayMode.BacksplashManufacturing
		|| navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
	if (isMfgView) {
		intersectionAreas = intersectionAreas.filter((area) => {
			return !area.intersectionReferenceIds?.some((refId) => {
				if (!refId || typeof refId !== 'string') return false;
				const refItem = kitchen.getReferenceMap(refId)?.panelItem;
				return refItem?.item?.placement === Components.PLACEMENT_TOP_ONLY && !refItem?.item?.distanceBelowCountertop;
			});
		});
	}

	// Plain SkuOverhead: keep only cutout gaps (cutout-to-edge / cutout-to-cutout); appliance gaps stay hidden.
	if (navigation.displayMode === DisplayMode.SkuOverhead) {
		const resolveRefCutout = (refId: string | null): { isItem: boolean; isPlainCutout: boolean } => {
			if (!refId || typeof refId !== 'string') return { isItem: false, isPlainCutout: false };
			const refItem = kitchen.getReferenceMap(refId)?.panelItem;
			if (!refItem?.item) return { isItem: false, isPlainCutout: false };
			const isPlainCutout = NON_APPLIANCE_CATEGORY_KEYS.has(refItem.item.category?.key ?? '')
				&& refItem.item.sku !== 'CUSTOM'
				&& !refItem.item.override;
			return { isItem: true, isPlainCutout };
		};
		intersectionAreas = intersectionAreas.filter((area) => {
			const states = (area.intersectionReferenceIds ?? []).map(resolveRefCutout);
			return states.some((s) => s.isPlainCutout) && !states.some((s) => s.isItem && !s.isPlainCutout);
		});
	}

	renderTracking.incrementRender('ShowCountertop');
	return (<g key={islandId} transform={`translate(${translate.x}, ${translate.y})`}>
		{showCountertops && <>
			{ /* Create countertop texture def */}
			{showTexture && <defs>
				<pattern id={countertopTextureId} x="0" y="0" height={materialScale.height} width={materialScale.width} patternUnits="userSpaceOnUse">
					<image href={`${options.assetsBaseUrl}/textures/${effectiveTexture}${options.assetsSasToken}`} x="0" y="0" width={materialScale.width} height={materialScale.height} />
				</pattern>
			</defs>}

			{ /* create cutout mask for countertop */ }
			{(showCutouts) &&
				<RenderCountertopMask islandId={islandId} />
			}

			{ /* Draw countertop */}
			{svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-context',
				fill: 'darkgray',
				stroke: 'none',
				strokeWidth: '.1',
				mask: countertopMaskUrl,
				opacity: blueprintToApplianceOpacity === 0 ? 1 : 0.65,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: panelMeta.countertop.id })
			})}
		</>}

		{ /* Draw hidden countertop layer for context menu */}
		{!showCountertops && <>
			{svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-context',
				opacity: 0,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: island.id })
			})}
		</>}

		{ /* Draw Countertop Texture */}
		{showCountertops && showTexture &&
			<>{svgPolygonFromVectors(countertopVectors, {
				key: 'countertop',
				fill: `url(#${countertopTextureId})`,
				mask: countertopMaskUrl,
				stroke: 'none',
				opacity: blueprintToApplianceOpacity,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: panelMeta.countertop.id })
			})}</>
		}

		{ /* Red tint marking a non-RTA placeholder countertop */}
		{showCountertops && showTexture && isNonCountertop &&
			<>{svgPolygonFromVectors(countertopVectors, {
				key: 'countertop-non-rta-tint',
				fill: NON_COUNTERTOP_TINT_COLOR,
				mask: countertopMaskUrl,
				stroke: 'none',
				opacity: NON_COUNTERTOP_TINT_OPACITY,
				...addContextMenu({ menuKey: BlueprintInstanceType.Countertop, id: panelMeta.countertop.id })
			})}</>
		}

		{ /* Show underlying panel */}
		{/* { blueprintToApplianceOpacity !== 1 && <> */}
		{/* {highlightFrontPanel &&
			<g fill="none" stroke="black" strokeWidth=".25" strokeDasharray="2 .5" opacity={options.showCountertops ? 0.25 : 0.75}>
				{drawSvgPolygonFromVectors(underlyingFrontPanel.vectors, { key: 'front-panel-frame' })}
			</g>
		} */}

		{ /* show corners */}
		{showCorners &&
			<g fill="lightgray" opacity={options.showCountertops ? 0.55 : 1}>
				{island.panelTrims.children.map((panelTrim) => {
					const { renderingDetails: ptRenderingDetails, center: panelTrimCenter } = panelTrim;
					return svgPolygonFromVectors(convertPolyPoints2Vectors(ptRenderingDetails), {
						key: panelTrim.id,
						transform: `translate(${panelTrimCenter.x}, ${panelTrimCenter.z})`
					});
				})}
			</g>
		}

		{
			underlyingPanels.map((panelData) => {
				const panelCenter = panelData.panel.center;
				return <g key={panelData.panel.id} transform={`translate(${panelCenter.x}, ${panelCenter.z}) rotate(${-panelData.panel.getOriginAngle()})`}>{
					svgPolygonFromVectors(
						panelData.vectors,
						{
							key: panelData.panel.id,
							fill: 'lightgrey',
							stroke: 'black',
							strokeWidth: '.0625',
							strokeDasharray: '2 .5',
							opacity: options.showCountertops ? 0.25 : 0.75,
							...addContextMenu({ menuKey: BlueprintInstanceType.Panel, id: panelData.panel.id, center: { x: panelCenter.x, y: panelCenter.z } })
						}
					) }

					{ /* display Appliances */}
					{(showAppliances || showCutouts) &&
						<ShowCountertopPanelItemGroups panelItemGroups={panelData.panel.panelItemGroups} />
					}
				</g>
			})
		}

		{/* <g fill="lightgrey" stroke="black" strokeWidth=".0625" strokeDasharray="2 .5" opacity={options.showCountertops ? 0.25 : 0.75}>
			{underlyingPanels.map((panels) => drawSvgPolygonFromVectors(panels.vectors, { key: panels.panel.id, ...addContextMenu({ menuKey: BlueprintInstanceType.Panel, id: panels.panel.id }) }))}
		</g> */}

		{/* </>} */}

		{ /* display Appliances */}
		{/* {(showAppliances || showCutouts) &&
			<ShowCountertopPanelItemGroups panelId={panelMeta._id} />
		} */}

		{ /* add panel outside dimensions */}
		{showPanelOutsideDimensions && island.panels.children.map((panel, i) => {
			// Joint-countertop sheet: the depth (side-panel) dimension never shows on either
			// member of a joint group — it's the same measurement drawn redundantly per top.
			if (hideDepthDimension && panel.isSidePanel) return null;
			// Joint-countertop sheet: the row draws one back-edge total split at the seams (#551), so a
			// member draws neither its own back-edge length nor its breakpoint segment dimensions.
			if (hideLengthDimension && panel.isBackPanel) return null;

			const hasConnections = navigation.displayMode === DisplayMode.Overhead &&
				(panel.hasSourceConnection || panel.hasTargetConnection);
			if (hasConnections) return null;

			const overhangOverride = showCountertops ? undefined : 0;
			const dimOverhang = overhangOverride ?? panel.countertopOverhang;
			const { corners, center: panelCenter } = panel.getSidePoints({ includeCountertop: true, overhangOverride, translate: true, rotate: true });
			let length = corners[0].getDistance(corners[1]);

			// Corner island dimension override: processSideConnection produces inconsistent
			// padding at 45° miter joints. All five panel dimensions are computed from known
			// geometry: back from structural width + overhangs, sides from cornerSize.depth +
			// overhangs, front from the Pythagorean relationship (hypotenuse of the corner).
			if (island.type === IslandType.Corner) {
				const cornerSize = island.size as CornerSize;
				const oh = showCountertops ? null : 0;
				if (panel.isFrontPanel) {
					// The front edge of the corner island countertop is the hypotenuse
					// of the right triangle formed by the back and side dimensions.
					const leftSide = frontPanel.getLeftPanel();
					const rightSide = frontPanel.getRightPanel();
					const backWidth = Math.max(...island.panels.getBackPanels().map((p) => p.size.width));
					const leftBack = leftSide?.getLeftPanel();
					const rightBack = rightSide?.getRightPanel();
					const frontOh = oh ?? frontPanel.countertopOverhang ?? 0;
					const leftBackLen = backWidth + (oh ?? leftBack?.getLeftPanel()?.countertopOverhang ?? 0) + (oh ?? leftBack?.getRightPanel()?.countertopOverhang ?? 0);
					const rightBackLen = backWidth + (oh ?? rightBack?.getLeftPanel()?.countertopOverhang ?? 0) + (oh ?? rightBack?.getRightPanel()?.countertopOverhang ?? 0);
					const leftSideLen = cornerSize.depth.left + (oh ?? leftSide?.getLeftPanel()?.countertopOverhang ?? 0) + frontOh - (oh ?? leftSide?.countertopOverhang ?? 0);
					const rightSideLen = cornerSize.depth.right + (oh ?? rightSide?.getRightPanel()?.countertopOverhang ?? 0) + frontOh - (oh ?? rightSide?.countertopOverhang ?? 0);
					const dx = leftBackLen - rightSideLen;
					const dy = rightBackLen - leftSideLen;
					length = Math.sqrt(dx * dx + dy * dy);
				} else if (panel.isBackPanel) {
					const leftAdj = panel.getLeftPanel();
					const rightAdj = panel.getRightPanel();
					const backWidth = Math.max(...island.panels.getBackPanels().map((p) => p.size.width));
					length = backWidth + (oh ?? leftAdj?.countertopOverhang ?? 0) + (oh ?? rightAdj?.countertopOverhang ?? 0);
				} else if (panel.isLeftPanel) {
					const backOverhang = oh ?? panel.getLeftPanel()?.countertopOverhang ?? 0;
					const frontOverhang = oh ?? frontPanel.countertopOverhang;
					const sideOverhang = oh ?? panel.countertopOverhang;
					length = cornerSize.depth.left + backOverhang + frontOverhang - sideOverhang;
				} else if (panel.isRightPanel) {
					const backOverhang = oh ?? panel.getRightPanel()?.countertopOverhang ?? 0;
					const frontOverhang = oh ?? frontPanel.countertopOverhang;
					const sideOverhang = oh ?? panel.countertopOverhang;
					length = cornerSize.depth.right + backOverhang + frontOverhang - sideOverhang;
				}
			}

			// Use panel center for positioning instead of position property
			// const panelCenter = panel.center;
			const panelAxis = panel.getOriginAngle();
			const isLevel = panelAxis === 0;
			// const isLevel = Math.abs(panelAxis % 180) < 45 || Math.abs(panelAxis % 180) > 135;

			if (hasBreakPoints && options.showBreakpoints && panel.isBackPanel) {
				const bpList = [...panelMeta.breakpoints].sort((a, b) => a.center - b.center);
				const leftEdgeX = Math.min(corners[0].x, corners[1].x);
				const rightEdgeX = Math.max(corners[0].x, corners[1].x);
				let prevBpPosition = leftEdgeX;

				const bpSegments = bpList.map((bp) => {
					const segWidth = bp.center - prevBpPosition;
					const segOffset = prevBpPosition;
					prevBpPosition = bp.center;
					return { width: segWidth, xOffset: segOffset };
				});

				// add end segment
				bpSegments.push({
					width: rightEdgeX - prevBpPosition,
					xOffset: prevBpPosition
				});

				const dimensionAxis = new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }});

				return <g key={`breakpoint-${i}`}>
					{/* overall panel dimension */}
					<DimensionLine
						length={length}
						persist={{ sourceId: panel.id, targetId: island.id }}
						axis={dimensionAxis}
						includeLabelBox={includeLabelBox}
						topView={true}
						canMove={true}
						dragConstraint="1d"
						bBox={{
							origin: panelCenter,
							size: { width: length, height: length, depth: length }
						}}
						padding={(dimOverhang / 2) + 3.75}
						indicatorStart="flat" indicatorEnd="flat"
						level={isLevel}
					/>

					{/* breakpoint segment dimensions -- a seam at the panel edge yields a segment
						spanning nearly the whole panel, which duplicates the overall dim above */}
					{bpSegments.filter((seg) => Math.abs(seg.width - length) > 0.5).map((seg, iBp) => {
						return <DimensionLine key={iBp}
							length={seg.width}
							axis={dimensionAxis}
							topView={true}
							canMove={true}
							bBox={{
								origin: new Vertex3({
									dataIn: {
										x: seg.xOffset + (seg.width / 2),
										y: panelCenter.y,
										z: panelCenter.z
									}
								}),
								size: { width: seg.width, height: length, depth: length }
							}}
							includeLabelBox={includeLabelBox}
							padding={(dimOverhang / 2) + 1.5}
							indicatorStart="flat" indicatorEnd="flat"
							level={isLevel}
						/>
					})}
				</g>
			}

			// countertop outside dimensions
			if ((i !== 0 && i !== 1) || ((i === 0 || i === 1) && island.type === IslandType.Corner)) {
				return <DimensionLine key={i}
					length={length}
					persist={{ sourceId: panel.id, targetId: island.id }}
					axis={new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }})}
					includeLabelBox={includeLabelBox}
					topView={true}
					canMove={true}
					dragConstraint="1d"
					bBox={{
						origin: panelCenter,
						size: { width: length, height: length, depth: length }
					}}
					padding={(dimOverhang / 2) + 1.5}
					indicatorStart="flat" indicatorEnd="flat"
					level={isLevel}
				/>
			}

			return null;
		})}

		{ /* render breakpoints */}
		{hasBreakPoints && options.showBreakpoints &&
			<RenderBreakpointsCountertop {...{ islandId: island.id }} />
		}

		{ /* Developer Layer / Middle */}
		{options.showDeveloperInsights && <>
			<DevShowIntersectionAreas areas={intersectionAreas} />
		</>}

		{ /* add panel inside dimensions */}
		{options.showPanelInsideDimensions && island.panels.children.map((panel, i) => {
			const panelAxis = panel.getOriginAngle();

			const { corners, center } = panel.getSidePoints({ includeCountertop: false, translate: true, rotate: true });
			let length = corners[0].getDistance(corners[1]);

			// Corner island inside dimension override: use exact structural values
			if (island.type === IslandType.Corner) {
				const cornerSize = island.size as CornerSize;
				if (panel.isFrontPanel) {
					length = panel.size.width;
				} else if (panel.isBackPanel) {
					length = panel.size.width;
				} else if (panel.isLeftPanel) {
					length = cornerSize.depth.left;
				} else if (panel.isRightPanel) {
					length = cornerSize.depth.right;
				}
			}

			const isCorner = island.type === IslandType.Corner;
			const dimensionOrigin = isCorner
				? center
				: new Vertex3({
					dataIn: {
						x: center.x * 0.5,
						y: center.y,
						z: center.z * 0.5
					}
				});

			return <DimensionLine key={i}
				persist={{ sourceId: `${panel.id}:inside`, targetId: island.id }}
				length={length}
				axis={new Axis({ dataIn: { x: 0, y: panelAxis, z: 0 }})}
				includeLabelBox={includeLabelBox}
				topView={true}
				canMove={true}
				bBox={{
					origin: dimensionOrigin,
					size: { width: length, height: length, depth: length }
				}}
				padding={-2}
				indicatorStart="arrow" indicatorEnd="arrow"
			/>
		})}

		{ /* add panel appliance dimensions */}
		{options.showDimensionsMode !== ShowDimensions.None && showApplianceDimensions && intersectionAreas.map((area, i) => {
			// console.log('intersectionArea:render', { labelFactor: area.dimension.labelFactor, lineFactor: area.dimension.lineFactor });
			// if (area.intersectionReferenceIds[2]) console.log('intersectionArea:render', area.axis.z === 0 ? area.size.width : area.size.height, area.intersectionReferenceIds);
			// Suffix the persist sourceId so the top-down (countertop) intersection dim resolves to a separate Dimension from the front-panel view -- otherwise hiding the label in the front-panel view also hides it here. Mirrors the per-item suffix in draw-panel-item-dimensions.tsx.
			return <DimensionLine key={i}
				persist={{ sourceId: `${area.intersectionReferenceIds[0]}:ct`, targetId: area.intersectionReferenceIds[1], alternateId: area.intersectionReferenceIds[2] }}
				axis={area.axis}
				length={area.axis.z === 0 ? area.size.width : area.size.height}
				canMove={true}
				bBox={{ origin: area.origin, size: area.size }}
				includeLabelBox={useDimensionBox} level={true}
				shouldHide = {area.shouldHide}
			/>;
		})}

		{ /* Top-model wording (e.g. kegerator tap) -- painted last so it sits above all appliance cutouts */}
		{(showAppliances || showCutouts) && <RenderCountertopTopModelLabels island={island} />}

		{ /* Off-panel magnified detail blocks for cutouts whose component opts into svg.offPanelDetail (GitHub #282/#385) */}
		{(showAppliances || showCutouts) && <RenderCountertopCutoutDetailBlocks island={island} />}

		{ /* render add island buttons when in overhead mode */}
		<AddIslandActions islandId={island.id} offset={{ x: offsetX, y: offsetY }} />

		{/* render delete island buttons when in overhead mode */}
		<IslandActions islandId={island.id} offset={{ x: offsetX, y: offsetY }} />

		{devInsightsLayer.cornerPoints}
		{devInsightsLayer.connectionPoints}
		{devInsightsLayer.islandOrigin}
	</g>);
};
